import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Shield, Lock, Eye, Users, FileText, Mail, MapPin, Phone, CheckCircle, Database, AlertTriangle, Clock } from 'lucide-react';

export default function PrivacyPolicy() {
 const privacyFeatures = [
 {
 icon: Shield,
 title: "Data Protection",
 description: "Compliant with Ethiopian Data Protection Proclamation"
 },
 {
 icon: Lock,
 title: "Secure Encryption",
 description: "Military-grade encryption for all personal data"
 },
 {
 icon: Eye,
 title: "Transparency",
 description: "Full control over your data and visibility"
 },
 {
 icon: Users,
 title: "Your Rights",
 description: "Access, correct, or delete your data anytime"
 }
 ];

 const userRights = [
 "Right to access your personal data",
 "Right to correct inaccurate information",
 "Right to request data deletion",
 "Right to object to data processing",
 "Right to data portability",
 "Right to withdraw consent"
 ];

 const dataCollection = [
 { type: "Personal Information", items: ["Name, email, phone number", "Profile photo, resume", "Employment history"] },
 { type: "Usage Data", items: ["IP address, browser type", "Page visits and interactions", "Device information"] },
 { type: "Cookies & Tracking", items: ["Session cookies", "Preference cookies", "Analytics data"] }
 ];

 return (
 <div className="min-h-screen ">
 {/* Hero Section */}
 <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-primary/10">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-4xl mx-auto">
 <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r from-primary to-primary/80 shadow-lg mb-4 sm:mb-6">
 <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
 </div>
 <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6">
 Privacy Policy
 </h1>
 <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 sm:mb-8">
 Your privacy is our priority. Learn how Shebeka collects, uses, and protects your personal information.
 </p>
 <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
 <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-100 text-green-800 dark:text-green-300 rounded-full text-xs sm:text-sm font-medium">
 GDPR Compliant
 </div>
 <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-100 text-blue-800 dark:text-blue-300 rounded-full text-xs sm:text-sm font-medium">
 Ethiopian Data Protection
 </div>
 <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 text-purple-800 dark:text-purple-300 rounded-full text-xs sm:text-sm font-medium">
 Last Updated: Jan 4, 2025
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* Main Content */}
 <section className="py-12 sm:py-16 md:py-20">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Privacy Features Grid */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
 {privacyFeatures.map((feature) => {
 const Icon = feature.icon;
 return (
 <Card
 key={feature.title}
 className="text-center hover:shadow-lg hover:scale-105 transition-all duration-300 border-border"
 >
 <CardContent className="pt-6 pb-5">
 <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
 <Icon className="w-6 h-6 text-primary" />
 </div>
 <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">{feature.title}</h3>
 <p className="text-xs sm:text-sm text-muted-foreground">{feature.description}</p>
 </CardContent>
 </Card>
 );
 })}
 </div>

 {/* Introduction */}
 <Card className="mb-8 sm:mb-12 border-primary/20">
 <CardContent className="p-4 sm:p-6 md:p-8">
 <div className="flex flex-col sm:flex-row items-start gap-4">
 <div className="flex-shrink-0">
 <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
 </div>
 <div>
 <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed mb-4">
 Welcome to <span className="font-bold text-primary">Shebeka's</span> Privacy Policy. This document outlines how we collect, use, disclose, and safeguard your personal information when you use our job portal platform.
 </p>
 <p className="text-sm sm:text-base text-muted-foreground">
 By using Shebeka, you agree to the collection and use of information in accordance with this policy. We are committed to protecting your privacy and ensuring transparency in our data practices.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Information We Collect */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Database className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Information We Collect
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
 {dataCollection.map((category) => (
 <Card key={category.type} className="border-border">
 <CardHeader>
 <CardTitle className="text-base sm:text-lg">{category.type}</CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {category.items.map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
 <span>{item}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>

 {/* How We Use Your Information */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Lock className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 How We Use Your Information
 </h2>
 <Card className="border-border">
 <CardContent className="p-4 sm:p-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {[
 "To create and manage your account",
 "To process job applications",
 "To match job seekers with opportunities",
 "To communicate about platform updates",
 "To improve our services and user experience",
 "To comply with legal obligations",
 "To prevent fraud and ensure security",
 "To provide customer support"
 ].map((item, idx) => (
 <div key={idx} className="flex items-start gap-3 p-3 rounded-lg hover: transition-colors">
 <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
 <span className="text-sm sm:text-base text-foreground">{item}</span>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Your Rights */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Users className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Your Privacy Rights
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {userRights.map((right, idx) => (
 <Card key={idx} className="hover:shadow-md transition-shadow border-border">
 <CardContent className="p-4 sm:p-5">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
 <span className="text-sm font-bold text-primary">{idx + 1}</span>
 </div>
 <p className="text-sm sm:text-base text-foreground font-medium">{right}</p>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>

 {/* Data Security */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Data Security Measures
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <Card className="border-border">
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
 <Lock className="w-5 h-5 text-primary" />
 Technical Safeguards
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {[
 "End-to-end encryption for sensitive data",
 "Secure HTTPS connection for all communications",
 "Regular security audits and penetration testing",
 "Firewalls and intrusion detection systems"
 ].map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
 {item}
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>

 <Card className="border-border">
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
 <Users className="w-5 h-5 text-primary" />
 Organizational Safeguards
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {[
 "Strict access controls and authentication",
 "Employee training on data protection",
 "Regular privacy impact assessments",
 "Incident response and breach notification procedures"
 ].map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
 {item}
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 </div>
 </div>

 {/* Data Retention */}
 <Card className="mb-8 sm:mb-12 bg-yellow-500/10 border-yellow-200 dark:border-yellow-800/30">
 <CardContent className="p-4 sm:p-6">
 <div className="flex items-start gap-4">
 <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-1" />
 <div>
 <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">Data Retention Policy</h3>
 <p className="text-sm sm:text-base text-muted-foreground">
 We retain your personal data only for as long as necessary to fulfill the purposes outlined in this privacy policy, comply with legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your account and data at any time.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Contact DPO */}
 <Card className="border-border shadow-lg">
 <CardHeader>
 <CardTitle className="text-xl sm:text-2xl text-center">Contact Our Data Protection Officer</CardTitle>
 <CardDescription className="text-center">
 For privacy-related inquiries, concerns, or to exercise your rights
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
 <div className="flex flex-col items-center text-center p-4 rounded-lg ">
 <Mail className="w-8 h-8 text-primary mb-3" />
 <p className="text-xs text-muted-foreground mb-1">Email</p>
 <a href="mailto:privacy@shebeka.com" className="text-sm sm:text-base font-medium text-primary hover:underline">
 privacy@shebeka.com
 </a>
 </div>
 <div className="flex flex-col items-center text-center p-4 rounded-lg ">
 <Phone className="w-8 h-8 text-primary mb-3" />
 <p className="text-xs text-muted-foreground mb-1">Phone</p>
 <a href="tel:+251111234567" className="text-sm sm:text-base font-medium text-primary hover:underline">
 +251 11 123 4567
 </a>
 </div>
 <div className="flex flex-col items-center text-center p-4 rounded-lg ">
 <MapPin className="w-8 h-8 text-primary mb-3" />
 <p className="text-xs text-muted-foreground mb-1">Address</p>
 <p className="text-sm sm:text-base font-medium text-foreground">
 Addis Ababa, Ethiopia
 </p>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 </section>

 {/* Final Note */}
 <section className="py-12 sm:py-16 ">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <Card className="bg-gradient-to-r from-primary to-primary/80 border-0 text-white">
 <CardContent className="p-6 sm:p-8">
 <div className="flex flex-col md:flex-row items-center justify-between gap-4">
 <div>
 <h3 className="text-lg sm:text-xl font-bold mb-2">Your Trust is Our Priority</h3>
 <p className="text-sm sm:text-base text-white/90">
 We are committed to protecting your privacy and maintaining the highest standards of data protection.
 </p>
 </div>
 <div className="text-xs sm:text-sm /20 backdrop-blur-sm rounded-xl px-3 sm:px-4 py-2 text-center">
 <Clock className="w-5 h-5 mx-auto mb-1" />
 <p className="font-bold">Updated Regularly</p>
 <p className="text-white/80">Last: Jan 4, 2025</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Quick Links */}
 <div className="mt-8 text-center">
 <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
 Related policies and information:
 </p>
 <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
 <a 
 href="/cookie-policy" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-card text-primary font-medium rounded-xl border border-primary/20 hover:bg-primary/5 transition-colors text-sm sm:text-base"
 >
 Cookie Policy
 </a>
 <a 
 href="/terms" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-card text-primary font-medium rounded-xl border border-primary/20 hover:bg-primary/5 transition-colors text-sm sm:text-base"
 >
 Terms of Service
 </a>
 <a 
 href="/contact" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 transition-all text-sm sm:text-base"
 >
 Contact Us
 </a>
 </div>
 </div>
 </div>
 </section>
 </div>
 );
}
